/**
 * From an analyst's mark in the change log to its History row (PRD-0075 R-C3, R-C4): what was
 * marked, the value and date, why, who, and — for a relationship — the entity on its other side,
 * seen from the history it is in. An extraction's `entity_retired` (an excerpt, a removed source)
 * is no analyst mark and reads as its label.
 */

import type { FactChangeRow } from '@/components/entity-history/fact-row-text'
import type { CaseEventDto, CaseEventKind } from '@/services/api/case-events'
import { historyActor } from './event-detail'
import { valueText } from './summary-view'

const FACT_KINDS: ReadonlySet<CaseEventKind> = new Set([
  'fact_asserted',
  'fact_invalidated',
  'entity_retired',
])
/** `entity_retired` reasons of an analyst's *Chybné* and of what fell with it (server `analyst_facts.py`). */
const MARKED_WRONG = 'marked_wrong'
const SOLE_SOURCE_RETIRED = 'sole_source_retired'

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

function targetOf(payload: Record<string, unknown>): FactChangeRow['target'] {
  return payload.target === 'relationship'
    ? 'relationship'
    : payload.target === 'property'
      ? 'property'
      : 'entity'
}

function isAnalystMark(kind: CaseEventKind, payload: Record<string, unknown>): boolean {
  if (kind !== 'entity_retired') return true
  return payload.reason === MARKED_WRONG || payload.reason === SOLE_SOURCE_RETIRED
}

/** The row of an analyst's mark; null for any other event. */
export function factChangeOf(
  event: CaseEventDto,
  kind: CaseEventKind | null,
  context: { uid: string | null; memberNames: ReadonlyMap<string, string> }
): FactChangeRow | null {
  const { payload } = event
  if (!kind || !FACT_KINDS.has(kind) || !isAnalystMark(kind, payload)) return null
  const target = targetOf(payload)
  // The events name the relationship's start as subject: from its end, the other side is the start.
  const fromObject = context.uid !== null && event.object_uid === context.uid
  return {
    target,
    key: text(payload.key),
    value: valueText(kind === 'fact_asserted' ? payload.new : payload.value),
    date: text(payload.invalid_from),
    note: text(kind === 'fact_invalidated' ? payload.reason : payload.note),
    relationship:
      target === 'relationship'
        ? {
            type: text(payload.type) ?? '',
            otherName: text(fromObject ? payload.subject_name : payload.object_name),
          }
        : null,
    reverted: payload.reverted === true,
    along: payload.reason === SOLE_SOURCE_RETIRED,
    actor: historyActor(event.actor, context.memberNames),
  }
}
